// src\modules\Products\CreateEdit\components\modals\AdjustStockModal.tsx
import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@shared/components/ui/button';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Input } from '@shared/components/ui/input';
import { StepperInput } from '@shared/components/ui/StepperInput';
import { useToast } from '@shared/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { ProductsApiAdapter } from '../../adapters/ProductsApiAdapter';

export interface AdjustStockModalRef {
  /**
   * Abre la modal de ajuste para una ubicación específica
   */
  open: (
    options: {
      productId: string | number;
      locationId: string | number;
      locationCode: string;
      currentQty?: number;
    },
    onConfirm?: (data: { unidades: number; descripcion: string }) => void,
  ) => void;
}

export const AdjustStockModal = forwardRef<AdjustStockModalRef>((_, ref) => {
  const { t } = useTranslation('createEditProductsModule');
  const modalRef = useRef<ModalCenterRef>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [productId, setProductId] = useState<string | number | null>(null);
  const [locationId, setLocationId] = useState<string | number | null>(null);
  const [locationCode, setLocationCode] = useState<string>('');
  const [unidades, setUnidades] = useState<number>(0);
  const [descripcion, setDescripcion] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const confirmRef = useRef<(data: { unidades: number; descripcion: string }) => void>(() => {});

  useImperativeHandle(ref, () => ({
    open: (opts, onConfirm) => {
      setProductId(opts.productId);
      setLocationId(opts.locationId);
      setLocationCode(opts.locationCode);
      setUnidades(0);
      setDescripcion('');
      confirmRef.current = onConfirm || (() => {});
      modalRef.current?.openModal();
    },
  }));

  const handleConfirm = async () => {
    if (!productId || !locationId || unidades <= 0) {
      toast({
        title: t('form.stock_adjust_error_title'),
        description: t('form.error_required'),
        variant: 'destructive',
      });
      return;
    }

    setSubmitting(true);
    try {
      const httpClient = createHttpClient(apiClientWH);
      const adapter = new ProductsApiAdapter(httpClient);
      await adapter.adjustStock({
        productId,
        locationId,
        quantity: unidades,
        description: descripcion,
      });

      toast({
        title: t('form.stock_adjust_success_title'),
        description: t('sections.stock_adjust'),
        variant: 'success',
      });

      await queryClient.invalidateQueries({
        queryKey: ['locations-by-product', productId],
      });

      confirmRef.current({ unidades, descripcion });
      modalRef.current?.closeModal();
    } catch (error: any) {
      const data = error?.response?.data;
      const apiMessage =
        data?.message ||
        data?.error?.message ||
        (Array.isArray(data?.errors) && data.errors[0]?.message) ||
        data?.error_description ||
        error?.message;
      toast({
        title: t('form.stock_adjust_error_title'),
        description: apiMessage || t('form.error_desc'),
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalCenter ref={modalRef} title={t('modal.adjust_stock_title')} type="default" width="lg" fullScreenMobile={true}>
      <div className="flex flex-col h-full">
        {/* Texto introductorio - Respetar formato solicitado */}
        <div className="text-sm mb-3">
          <p>
            {t('modal.adjust_stock_intro')} <span className="font-medium text-title">{locationCode}</span>.
          </p>
          <p>{t('modal.adjust_stock_note')}</p>
        </div>

        <div className="flex-1 overflow-y-auto space-y-6 mt-1">
          <div className="grid grid-cols-1 md:grid-cols-10 gap-4">
            <div className="md:col-span-3 max-w-[180px]">
              <label className="block text-sm mb-2 text-text">{t('modal.unidades_label')}</label>
              <StepperInput
                value={unidades}
                onChange={setUnidades}
                min={0}
                max={999999}
                data-testid="adjust-stock-units-stepper"
              />
            </div>
            <div className="md:col-span-7">
              <label className="block text-sm mb-2 text-text">{t('modal.descripcion_label')}</label>
              <Input
                placeholder={t('modal.descripcion_placeholder')}
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                data-testid="adjust-stock-description-input"
              />
            </div>
          </div>
        </div>

        {/* Botonera */}
        <div className="flex justify-end gap-2 pt-4">
          <Button
            variant="outline"
            size="lg"
            onClick={() => modalRef.current?.closeModal()}
            data-testid="adjust-stock-cancel-button"
          >
            {t('modal.cancelar')}
          </Button>
          <Button
            variant="primary"
            size="lg"
            onClick={handleConfirm}
            disabled={unidades <= 0 || submitting || !productId || !locationId}
            data-testid="adjust-stock-confirm-button"
          >
            {t('modal.ajustar')}
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

AdjustStockModal.displayName = 'AdjustStockModal';
